import { useState } from 'react'
import { supabase } from '../services/supabase'
import {
    calcularDataInicialRecorrencia,
    normalizarPeriodicidade,
} from '../utils/periodicidadePix'

export interface CriarCobrancaPixParams {
    valor: number
    chave: string
    solicitacaoPagador?: string
    expiracao?: number
}

export interface CobrancaPixResponse {
    txid: string
    qrcode: string
    imagemQrcode?: string
    location: string
    valor: string
    expiracao: number
    status: string
}

export interface PixStatusResponse {
    txid: string
    status: string
    valor?: string
    pagador?: {
        nome?: string
        cpf?: string
        cnpj?: string
    }
    pix?: Array<{
        endToEndId: string
        txid: string
        valor: string
        horario: string
    }>
}

export interface CriarRecorrenciaPixParams {
    valor: number
    chave?: string
    descricao: string
    devedor_cpf: string
    devedor_nome: string
    /** SEMANAL | MENSAL | TRIMESTRAL | SEMESTRAL | ANUAL */
    periodicidade?: string
}

export interface RecorrenciaPixResponse {
    idRec: string
    status: string
    valor: number
    periodicidade: string
    dataInicio: string
    location: string
    locationId: number
    tipoJornada?: string
    jornada?: number
    txidCobrancaImediata?: string
    qrcode?: string | null
    imagemQrcode?: string | null
    mensagem?: string
}

export interface RecorrenciaDetalheResponse {
    idRec: string
    status: string
    valor?: {
        valorRec?: string
    }
    calendario?: {
        dataInicial?: string
        periodicidade?: string
    }
    loc?: {
        id?: number
        location?: string
    }
}

export interface SolicitacaoRecorrenciaParams {
    idRec: string
    valorInicial?: number
    txid?: string
}

export interface SolicitacaoRecorrenciaResponse {
    idSolicitacao?: string
    idRec: string
    qrcode: string | null
    imagemQrcode?: string | null
    location: string | null
    status: string
    valor: string
    mensagem: string
    txidCobrancaImediata?: string
    tipoJornada?: string
    jornada?: number
    error?: string
    httpStatus?: number
    tipo?: string
    txid?: string
    aviso?: string
    sugestoes?: string[]
    dadosRecorrencia?: {
        idRec: string
        status: string
        valor: string
        dataInicio: string
        periodicidade: string
    }
}

export function useEfiPix() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    /**
     * Cria uma cobrança PIX via Backend Node.js
     */
    const criarCobrancaPix = async (
        params: CriarCobrancaPixParams
    ): Promise<CobrancaPixResponse | null> => {
        setLoading(true)
        setError(null)

        try {
            // URL do backend (altere para sua URL do Railway após deploy)
            const backendUrl = import.meta.env.VITE_BACKEND_PIX_URL || 'http://localhost:3003'

            // Obter token do Supabase para autenticação
            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            // Chamar Backend Node.js
            const response = await fetch(`${backendUrl}/api/pix/criar-cobranca`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token || 'no-token'}`
                },
                body: JSON.stringify({
                    valor: params.valor,
                    chave: params.chave,
                    solicitacaoPagador: params.solicitacaoPagador || `Pagamento Latera - Plano`,
                    expiracao: params.expiracao || 3600
                })
            })

            const data = await response.json()

            // Se há erro na resposta
            if (!response.ok) {
                const errorMessage = data.error || 'Erro ao criar cobrança PIX'
                console.error('Erro no backend:', data)
                setError(errorMessage)
                return null
            }

            return data as CobrancaPixResponse
        } catch (err: any) {
            console.error('Erro ao criar cobrança PIX:', err)

            let errorMessage = 'Erro ao criar cobrança PIX'

            if (err?.message) {
                errorMessage = err.message
            } else if (err?.error_description) {
                errorMessage = err.error_description
            } else if (typeof err === 'string') {
                errorMessage = err
            }

            setError(errorMessage)
            return null
        } finally {
            setLoading(false)
        }
    }

    /**
     * Consulta o status de uma cobrança PIX via Backend Node.js
     */
    const consultarStatusPix = async (
        txid: string
    ): Promise<PixStatusResponse | null> => {
        setLoading(true)
        setError(null)

        try {
            // URL do backend
            const backendUrl = import.meta.env.VITE_BACKEND_PIX_URL || 'http://localhost:3003'

            // Obter token do Supabase para autenticação
            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            // Chamar Backend Node.js
            const response = await fetch(`${backendUrl}/api/pix/status/${txid}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token || 'no-token'}`
                }
            })

            const data = await response.json()

            // Se há erro na resposta
            if (!response.ok) {
                const errorMessage = data.error || 'Erro ao consultar status do PIX'
                console.error('Erro no backend:', data)
                setError(errorMessage)
                return null
            }

            return data as PixStatusResponse
        } catch (err: any) {
            console.error('Erro ao consultar status do PIX:', err)
            const errorMessage = err?.message || 'Erro ao consultar status do PIX'
            setError(errorMessage)
            return null
        } finally {
            setLoading(false)
        }
    }

    /**
     * Cria uma recorrência PIX (Pix Automático) via Backend Node.js
     */
    const criarRecorrenciaPix = async (
        params: CriarRecorrenciaPixParams
    ): Promise<RecorrenciaPixResponse | null> => {
        setLoading(true)
        setError(null)

        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
                import.meta.env.VITE_BACKEND_PIX_URL ||
                'http://localhost:3005'

            // Obter token do Supabase para autenticação (opcional)
            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            const periodicidade = normalizarPeriodicidade(params.periodicidade)
            const dataInicial = calcularDataInicialRecorrencia(periodicidade)

            const requestBody = {
                vinculo: {
                    contrato: `LATERA-${Date.now()}`,
                    devedor: {
                        cpf: params.devedor_cpf,
                        nome: params.devedor_nome
                    },
                    objeto: params.descricao
                },
                calendario: {
                    periodicidade,
                    dataInicial,
                },
                valor: {
                    valorRec: params.valor.toFixed(2)
                },
                valorImediato: params.valor.toFixed(2),
                politicaRetentativa: 'NAO_PERMITE'
            }

            const response = await fetch(`${backendUrl}/api/pix-auto/recorrencias`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify(requestBody)
            })

            const data = await response.json()

            if (!response.ok) {
                // Extrair mensagem de erro mais detalhada
                let errorMessage = 'Erro ao criar recorrência PIX'
                
                if (data.error) {
                    if (typeof data.error === 'string') {
                        errorMessage = data.error
                    } else if (data.error.mensagem) {
                        errorMessage = data.error.mensagem
                    } else if (data.error.message) {
                        errorMessage = data.error.message
                    } else {
                        errorMessage = JSON.stringify(data.error)
                    }
                } else if (data.mensagem) {
                    errorMessage = data.mensagem
                } else if (data.message) {
                    errorMessage = data.message
                } else if (data.details) {
                    errorMessage = data.details
                }
                
                console.error('❌ Erro no backend:', {
                    status: response.status,
                    statusText: response.statusText,
                    data: data
                })
                
                // Log detalhado do erro para debug
                if (data.details) {
                    console.error('📋 Detalhes do erro:', data.details)
                }
                if (data.error) {
                    console.error('📋 Mensagem de erro:', data.error)
                }
                
                setError(errorMessage)
                return null
            }

            const mapped: RecorrenciaPixResponse = {
                idRec: data.idRec,
                status: data.status || 'CRIADA',
                valor: Number(data?.valor?.valorRec || params.valor),
                periodicidade: data?.calendario?.periodicidade || periodicidade,
                dataInicio: data?.calendario?.dataInicial || dataInicial || data?.loc?.criacao || new Date().toISOString(),
                location: data?.loc?.location || '',
                locationId: data?.loc?.id || 0,
                tipoJornada: data?.ativacao?.dadosJornada?.tipoJornada || data?.tipoJornada || 'JORNADA_3',
                jornada: data?.jornada || 3,
                txidCobrancaImediata: data?.txidCobrancaImediata || data?.cobrancaImediata?.txid,
                qrcode: data?.dadosQR?.pixCopiaECola || null,
                mensagem: data?.dadosQR?.pixCopiaECola
                    ? 'Recorrencia criada com QR Code de pagamento imediato.'
                    : 'Recorrencia criada. Gerando QR Code...'
            }

            return mapped
        } catch (err: any) {
            console.error('❌ Erro ao criar recorrência PIX:', err)
            
            // Tratar diferentes tipos de erro
            let errorMessage = 'Erro ao criar recorrência PIX'
            
            if (err.message) {
                if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                    errorMessage = 'Erro de conexão com o servidor. Verifique se o backend Pix Auto está rodando na porta 3005.'
                } else if (err.message.includes('JSON')) {
                    errorMessage = 'Erro ao processar resposta do servidor.'
                } else {
                    errorMessage = err.message
                }
            }
            
            setError(errorMessage)
            return null
        } finally {
            setLoading(false)
        }
    }

    /**
     * Cria uma solicitação de confirmação da recorrência (gera QR Code)
     */
    const criarSolicitacaoRecorrencia = async (
        params: SolicitacaoRecorrenciaParams
    ): Promise<SolicitacaoRecorrenciaResponse | null> => {
        setLoading(true)
        setError(null)

        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
                import.meta.env.VITE_BACKEND_PIX_URL ||
                'http://localhost:3005'

            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            const response = await fetch(`${backendUrl}/api/pix-auto/recorrencias/${params.idRec}/solicitacao`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    valorInicial: params.valorInicial,
                    txid: params.txid
                })
            })

            const data = await response.json()

            if (!response.ok) {
                const errorMessage = data?.error || 'Erro ao gerar QR Code da recorrência'
                setError(errorMessage)
                return null
            }

            return {
                idRec: data.idRec,
                qrcode: data.qrcode || null,
                imagemQrcode: data.imagemQrcode || null,
                location: data.location || null,
                status: data.status || 'CRIADA',
                valor: data.valor || (params.valorInicial ? params.valorInicial.toFixed(2) : '0.00'),
                txidCobrancaImediata: data.txidCobrancaImediata || params.txid,
                tipoJornada: data.tipoJornada || 'JORNADA_3',
                jornada: data.jornada || 3,
                mensagem: data.mensagem || 'QR Code gerado com sucesso'
            }
        } catch (err: any) {
            console.error('❌ Erro ao criar solicitação de confirmação:', err)
            
            let errorMessage = 'Erro ao criar solicitação de confirmação'
            
            if (err.message) {
                if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                    errorMessage = 'Erro de conexão com o servidor. Verifique se o backend Pix Auto está rodando na porta 3005.'
                } else {
                    errorMessage = err.message
                }
            }
            
            setError(errorMessage)
            return null
        } finally {
            setLoading(false)
        }
    }

    const consultarRecorrenciaPix = async (
        idRec: string
    ): Promise<RecorrenciaDetalheResponse | null> => {
        setLoading(true)
        setError(null)

        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
                import.meta.env.VITE_BACKEND_PIX_URL ||
                'http://localhost:3005'

            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            const response = await fetch(`${backendUrl}/api/pix-auto/recorrencias/${idRec}`, {
                method: 'GET',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                }
            })

            const data = await response.json()
            if (!response.ok) {
                setError(data?.error || 'Erro ao consultar recorrência')
                return null
            }

            return data as RecorrenciaDetalheResponse
        } catch (err: any) {
            setError(err?.message || 'Erro ao consultar recorrência')
            return null
        } finally {
            setLoading(false)
        }
    }

    return {
        criarCobrancaPix,
        consultarStatusPix,
        criarRecorrenciaPix,
        criarSolicitacaoRecorrencia,
        consultarRecorrenciaPix,
        loading,
        error
    }
}

